import { InputGroup, InputLeftAddon, Grid, Input, useDisclosure, InputRightElement } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { ControllableDrawer } from 'components/Drawer';
import ReactSelect, { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import FilterEmployeeAndGroupList from 'containers/Scheduling/RightDrawer/FilterEmployeeAndGroupList';
import FEATURES from 'global/features';
import { roles } from 'global/rolesAndPermissions';
import { fetchPaginatedJobs } from 'hooks/jobs/useFetchPaginatedJobs';
import { useFeatureList } from 'store/useFeatureStore';
import { useUserObject } from 'store/useUserStore';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

interface IFilterGroupProps {
  handleStatusChange: (selectedValue: ILabelValueOptions) => void;
  handleJobChange: (e: any) => void;
  handleEmployeeFilterFormSubmit: (values: any) => void;
}

const FilterGroup: React.FC<IFilterGroupProps> = ({
  handleStatusChange,
  handleEmployeeFilterFormSubmit,
  handleJobChange,
}) => {
  const { t } = useTranslation(['common']);

  const userStore = useUserObject();
  const featureList: any = useFeatureList();

  const [employeeListFormData, setEmployeeListFormData] = useState({ user_ids: [] as any, group_ids: [] as any });

  const {
    isOpen: isEmployeeListDrawerOpen,
    onOpen: onEmployeeListDrawerOpen,
    onClose: onEmployeeListDrawerClose,
  } = useDisclosure();

  const statusOptions = useMemo(
    () => [
      { label: t('common:all'), value: 'all' },
      { label: t('common:draft'), value: 'draft' },
      { label: t('common:published'), value: 'published' },
      { label: t('common:repeating'), value: 'repeating' },
    ],
    [t]
  );

  const jobLoadOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedJobs, { search, page, job_status: 1 });
  };

  const handleFormSubmit = (values: any) => {
    onEmployeeListDrawerClose();
    setEmployeeListFormData(values);
    handleEmployeeFilterFormSubmit(values.user_ids);
  };

  return (
    <Grid
      templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', md: '220px 280px 240px 1fr' }}
      gap="4"
      sx={{ '.valueContainerWrapper': { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } }}
      mb="4"
      className="hidden-print"
    >
      {userStore?.userInfo?.roleId !== roles.EMPLOYEE || userStore?.permissions?.manageSchedules ? (
        <InputGroup>
          <InputLeftAddon children="Status" fontSize="sm" />
          <ReactSelect
            options={statusOptions}
            size="md"
            variant="outline"
            fontSize="sm"
            isClearable
            isSearchable
            placeholder={t('common:select')}
            onChange={handleStatusChange}
          />
        </InputGroup>
      ) : null}
      {userStore?.userInfo?.roleId !== roles.EMPLOYEE ? (
        <InputGroup>
          <InputLeftAddon children="Users" fontSize="sm" />
          <Input
            placeholder={t('common:selectUsers')}
            fontSize="sm"
            value={`${employeeListFormData.user_ids.length || t('common:all')} ${t('common:users')}`}
            onChange={() => null}
            color={employeeListFormData.user_ids.length ? 'primary.900' : 'gray.400'}
            pr="60px"
          />
          <InputRightElement
            width="60px"
            children={
              <Button size="xs" onClick={onEmployeeListDrawerOpen}>
                {t('common:select')}
              </Button>
            }
          />
        </InputGroup>
      ) : null}
      {featureList[FEATURES.jobs] && (
        <InputGroup>
          <InputLeftAddon children="Job" fontSize="sm" />
          <ReactSelectAsyncPaginate
            loadOptions={jobLoadOptions}
            additional={{
              page: 1,
            }}
            size="md"
            variant="filled"
            fontSize="sm"
            onChange={handleJobChange}
            debounceTimeout={300}
            isClearable
            isSearchable
            placeholder={t('common:selectJob')}
            getOptionLabel={(option: any) => option.name}
            getOptionValue={(option: any) => option.id}
          />
        </InputGroup>
      )}
      <ControllableDrawer isOpen={isEmployeeListDrawerOpen} onClose={onEmployeeListDrawerClose}>
        <Formik enableReinitialize initialValues={employeeListFormData} onSubmit={handleFormSubmit}>
          {() => (
            <Form noValidate autoComplete="off">
              <FilterEmployeeAndGroupList />
            </Form>
          )}
        </Formik>
      </ControllableDrawer>
    </Grid>
  );
};

export default memo(FilterGroup);
